import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { FadeIn } from "@/components/FadeIn";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JournalDetailPage() {
  const { slug } = useParams();
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/journals/${slug}`).then((r) => { setJournal(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Loading...</p></div>;
  if (!journal) return <div className="min-h-screen flex items-center justify-center"><p className="font-body text-sm text-muted-foreground">Journal entry not found.</p></div>;

  return (
    <div data-testid="journal-detail-page">
      {/* Banner */}
      {journal.featured_image && (
        <div className="w-full max-h-[45vh] overflow-hidden">
          <img src={journal.featured_image} alt={journal.title} className="w-full h-full object-cover" />
        </div>
      )}

      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          <FadeIn>
            <Link to="/watch-read" className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-watch-read">
              <ArrowLeft size={14} strokeWidth={1.5} /> Back to Watch & Read
            </Link>
          </FadeIn>

          <FadeIn>
            <div className="mb-12">
              <div className="flex gap-2 mb-4">
                {journal.tags?.map((tag) => (
                  <Badge key={tag} variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[hsl(48,12%,88%)] text-muted-foreground">
                    {tag}
                  </Badge>
                ))}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tight font-light text-foreground mb-4">
                {journal.title}
              </h1>
              <p className="font-body text-sm text-muted-foreground">
                {new Date(journal.publish_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
          </FadeIn>

          <FadeIn>
            <div className="space-y-5">
              {journal.content?.split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="font-body text-base md:text-lg leading-[1.85] text-muted-foreground">
                  {p}
                </p>
              ))}
            </div>
          </FadeIn>

          {/* Substack link */}
          {journal.substack_url && (
            <>
              <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
              <FadeIn>
                <p className="font-body text-sm text-muted-foreground">
                  Also available on{" "}
                  <a href={journal.substack_url} target="_blank" rel="noopener noreferrer" className="text-[#5C6B5D] underline underline-offset-4 decoration-1">
                    Substack
                  </a>
                </p>
              </FadeIn>
            </>
          )}

          {/* Subscribe note */}
          <Separator style={{ backgroundColor: "#E5E4DE" }} className="my-12" />
          <FadeIn>
            <div className="text-center">
              <p className="font-body text-sm text-muted-foreground">
                Receive new writings by email. Subscribe on{" "}
                <a href="https://substack.com" target="_blank" rel="noopener noreferrer" className="text-[#5C6B5D] underline underline-offset-4 decoration-1">
                  Substack
                </a>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
